import { fg } from '@atlaskit/platform-feature-flags/fg';
import { createSentryLogger } from '@atlassian/smart-knowledge-cards/sentry-logger';

import { PRODUCT_BUILD } from '../../constants/build';
import { getEnvironment } from '../environment';
import { SENTRY_DSN, UNKNOWN_TAG } from '../sentry/constants';

import { logLegacyError } from './legacy-logger';

export type LogProps = Parameters<ReturnType<typeof createSentryLogger>['error']>[0] & {
	/** Retained only for the rollout's legacy logger. New instrumentation omits this. */
	functionName?: string;
	legacyError?: unknown;
	telemetryOnly?: boolean;
};

const sharedLogger = createSentryLogger({
	dsn: SENTRY_DSN,
	getEnvironment,
	packageName: '@atlassian/search-ai',
	release: PRODUCT_BUILD || UNKNOWN_TAG,
});

const log =
	(level: 'error' | 'warning' | 'info') =>
	async (props: LogProps | Parameters<typeof logLegacyError>[0]): Promise<void> => {
		// Keep unmigrated callers on their original transport until the experience PRs land.
		if (!('feature' in props)) {
			return logLegacyError(props);
		}
		if (!fg('kd_search_ai_robust_sentry_logs')) {
			if (props.functionName) {
				return logLegacyError({
					error: props.legacyError ?? props.error,
					functionName: props.functionName,
					traceId: props.traceId,
				});
			}
			return;
		}
		const { functionName, legacyError, telemetryOnly, ...event } = props;
		if (telemetryOnly || (event.error instanceof Error && event.error.name === 'AbortError')) {
			return;
		}
		if (event.error instanceof SyntaxError) {
			return sharedLogger[level]({
				...event,
				error: undefined,
				category: 'parse',
				message: 'Response could not be parsed',
			});
		}
		return sharedLogger[level](event);
	};

export const logger: Record<
	'error' | 'warning' | 'info',
	(props: LogProps | Parameters<typeof logLegacyError>[0]) => Promise<void>
> = {
	error: log('error'),
	warning: log('warning'),
	info: log('info'),
};
